import { useCallback, useEffect, useRef } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { EventVenueMap } from 'src/components/Events/VenueMap/EventVenueMap';
import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosSelect } from 'src/core/POS/PosSelect';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Stack } from 'src/core/ui';
import { TicketGroupInputTable } from 'src/modals/PurchaseWizard/PurchaseTicketsFlowV2/TicketGroupInputTable/TicketGroupInputTable';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { posChangedField, posField } from 'src/utils/posFieldUtils';
import { PurchaseTicketsInput } from 'src/utils/purchaseUtils';
import {
  DeliveryType,
  SectionInfo,
  TicketGroupInput,
} from 'src/WebApiController';

import { EventGroupTicketGroupDraft } from '../EventGroup.types';
import { PlaceholderEventMapContextProvider } from '../PlaceholderEventMapContextProvider';
import { EventGroupInfoStepOptions } from './EventGroupInfoStep.hooks';
import * as styles from './EventGroupTicketGroupsStep.css';

type EventGroupTicketGroupsStepProps = {
  venueConfigId: number | null;
  onVenueConfigChange: (venueConfigId: number | null) => void;
  ticketGroups: EventGroupTicketGroupDraft[];
  onChange: (tgs: EventGroupTicketGroupDraft[]) => void;
  currencyCode: string;
  costPerEvent?: number | null;
  /** Venue/venue-config search state + options, owned by the parent. */
  options: EventGroupInfoStepOptions;
};

type EventGroupVenueMapProps = {
  ticketGroups: EventGroupTicketGroupDraft[];
  onAddTicketGroup: (section: string) => void;
};

const EventGroupVenueMap = ({
  ticketGroups,
  onAddTicketGroup,
}: EventGroupVenueMapProps) => {
  const { venueMapInfo, isMapLoading } = useEventMapContext();

  const selectedSectionIds = venueMapInfo?.sections
    ?.filter((s: SectionInfo) =>
      ticketGroups.some((tg) => tg.section === s.name)
    )
    .map((s: SectionInfo) => s.id);

  const onSectionClicked = useCallback(
    (_e: MouseEvent, selectedSection: SectionInfo) => {
      const alreadyAdded = ticketGroups.some(
        (tg) => tg.section === selectedSection.name
      );
      if (!alreadyAdded) {
        onAddTicketGroup(selectedSection.name);
      }
    },
    [ticketGroups, onAddTicketGroup]
  );

  if (isMapLoading) {
    return <PosSpinner />;
  }

  return (
    <EventVenueMap
      selectedSectionIds={selectedSectionIds}
      onSectionClicked={onSectionClicked}
      isHeatMap={false}
    />
  );
};

let draftIdCounter = -1;

const newDraft = (
  currencyCode: string,
  costPerEvent?: number | null
): EventGroupTicketGroupDraft => ({
  id: draftIdCounter--,
  section: '',
  row: '',
  quantity: 1,
  unitCost: costPerEvent ?? 0,
  seatFrom: '',
  seatTo: '',
  deliveryType: DeliveryType.PDF,
  currencyCode,
});

export const draftToTicketGroupInput = (
  draft: EventGroupTicketGroupDraft
): TicketGroupInput => ({
  ticketGroupId: draft.id,
  purchaseOrderId: null,
  event: null,
  performer: null,
  venue: null,
  canGetDocArts: null,
  viagogoEventId: null,
  eventMappingId: null,
  viagogoVirtualId: String(draft.id),
  posEventId: null,
  unitCost: posField(draft.unitCost),
  unitFullyLoadedCost: null,
  inHandDate: posField(null),
  expectedValue: posField(null),
  isFaceValueCost: posField(null),
  faceValueCost: posField(null),
  deliveryType: posField(draft.deliveryType),
  totalCost: posField(null),
  taxPaid: posField(null),
  deliveryCost: posField(null),
  currencyCode: posField(draft.currencyCode),
  section: posChangedField(draft.section),
  sectionId: posChangedField(null),
  rowId: posChangedField(null),
  row: posChangedField(draft.row || null),
  tickets: posChangedField(null),
  numberOfTickets: posChangedField(draft.quantity),
  ticketTypeRules: null,
  internalNotes: posChangedField(null),
  listingNotes: posChangedField(null),
  seatIncementType: null,
  allocateSeats: null,
  webPrice: null,
  autoBroadcastCreatedListing: null,
  isTaxExempt: posChangedField(null),
});

const ticketGroupInputToDraft = (
  tg: TicketGroupInput,
  fallbackCurrencyCode: string
): EventGroupTicketGroupDraft => ({
  id: tg.ticketGroupId ?? draftIdCounter--,
  section: tg.section?.value ?? '',
  row: tg.row?.value ?? '',
  quantity: tg.numberOfTickets?.value ?? 1,
  unitCost: tg.unitCost?.value ?? 0,
  seatFrom: '',
  seatTo: '',
  deliveryType: tg.deliveryType?.value ?? DeliveryType.PDF,
  currencyCode: tg.currencyCode?.value ?? fallbackCurrencyCode,
});

export const EventGroupTicketGroupsStep = ({
  venueConfigId,
  onVenueConfigChange,
  ticketGroups,
  onChange,
  currencyCode,
  costPerEvent,
  options,
}: EventGroupTicketGroupsStepProps) => {
  const selectVenueConfigPlaceholder = useContent(
    ContentId.SelectVenueConfiguration
  );
  const loadingConfigsPlaceholder = useContent(ContentId.LoadingConfigurations);
  const enterVenueConfigIdPlaceholder = useContent(
    ContentId.EnterVenueConfigId
  );

  const initialTicketGroups =
    ticketGroups.length > 0
      ? ticketGroups.map(draftToTicketGroupInput)
      : [draftToTicketGroupInput(newDraft(currencyCode, costPerEvent))];

  const form = useForm<PurchaseTicketsInput>({
    defaultValues: {
      ticketGroups: initialTicketGroups,
      daysBeforeEvent: undefined,
      ticketGroupFromPartialPurchaseOrder: null,
      eventSearchTextFromPartialPurchaseOrder: null,
      updateSalesOfListings: null,
      updatePredeliveryETicketArtifacts: null,
    },
  });

  const fieldArray = useFieldArray({
    control: form.control,
    name: 'ticketGroups',
  });

  // Track previous numberOfTickets values to detect quantity changes
  const prevQtyRef = useRef<(number | null | undefined)[]>([]);

  // Distributes costPerEvent evenly across ALL tickets (not per ticket group):
  // unitCost = costPerEvent / totalTickets (same for every group), and each
  // group's totalCost = unitCost * its quantity.
  const applyCostDistribution = useCallback(
    (tgs: TicketGroupInput[]) => {
      if (costPerEvent == null || costPerEvent <= 0) return;
      const totalTickets = tgs.reduce(
        (sum, tg) => sum + (tg?.numberOfTickets?.value ?? 0),
        0
      );
      if (totalTickets <= 0) return;
      const unitCost = costPerEvent / totalTickets;
      tgs.forEach((tg, i) => {
        const qty = tg?.numberOfTickets?.value ?? 0;
        if (tg?.unitCost?.value !== unitCost) {
          form.setValue(`ticketGroups.${i}.unitCost`, posField(unitCost, true));
        }
        const total = unitCost * qty;
        if (tg?.totalCost?.value !== total) {
          form.setValue(`ticketGroups.${i}.totalCost`, posField(total, true));
        }
      });
    },
    [form, costPerEvent]
  );

  // Sync to parent on every form change, and redistribute cost whenever a
  // quantity (or the number of ticket groups) changes.
  useEffect(() => {
    const sub = form.watch((value) => {
      const tgs = (value.ticketGroups ?? []) as TicketGroupInput[];

      const qtys = tgs.map((tg) => tg?.numberOfTickets?.value);
      const qtyChanged =
        qtys.length !== prevQtyRef.current.length ||
        qtys.some((q, i) => q !== prevQtyRef.current[i]);
      if (qtyChanged) {
        prevQtyRef.current = qtys;
        applyCostDistribution(tgs);
      }

      // Sync to parent
      const drafts = tgs
        .filter(Boolean)
        .map((tg) => ticketGroupInputToDraft(tg, currencyCode));
      onChange(drafts);
    });
    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, onChange, currencyCode, applyCostDistribution]);

  // Redistribute when the per-event cost changes.
  useEffect(() => {
    applyCostDistribution(form.getValues('ticketGroups'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costPerEvent]);

  const onAddFromMap = useCallback(
    (sectionName: string) => {
      const draft = {
        ...newDraft(currencyCode, costPerEvent),
        section: sectionName,
      };
      fieldArray.append(draftToTicketGroupInput(draft));
    },
    [fieldArray, currencyCode, costPerEvent]
  );

  return (
    <FormProvider {...form}>
      <PlaceholderEventMapContextProvider venueConfigId={venueConfigId}>
        <Stack
          direction="column"
          gap="l"
          width="full"
          className={styles.container}
        >
          <PosFormField label={<Content id={ContentId.VenueConfiguration} />}>
            {options.hasVenueConfigOptions ? (
              <PosSelect
                showFullWidth
                searchable
                loading={options.isVenueConfigLoading}
                value={
                  venueConfigId != null ? String(venueConfigId) : undefined
                }
                valueOptionsContent={options.venueConfigOptions}
                onChange={(val) =>
                  onVenueConfigChange(val ? Number(val) : null)
                }
                placeholderText={selectVenueConfigPlaceholder}
              />
            ) : (
              <PosTextField
                type="number"
                min={1}
                value={venueConfigId != null ? String(venueConfigId) : ''}
                onChange={(e) =>
                  onVenueConfigChange(
                    e.target.value ? Number(e.target.value) : null
                  )
                }
                placeholder={
                  options.isVenueConfigLoading
                    ? loadingConfigsPlaceholder
                    : enterVenueConfigIdPlaceholder
                }
                disabled={options.isVenueConfigLoading}
              />
            )}
          </PosFormField>
          {venueConfigId != null && (
            <div className={styles.venueMapContainer}>
              <EventGroupVenueMap
                ticketGroups={ticketGroups}
                onAddTicketGroup={onAddFromMap}
              />
            </div>
          )}
          {costPerEvent != null && (
            <div className={styles.costSummaryContainer}>
              <FormatContent
                id={FormatContentId.CostPerEventDistributed}
                params={[costPerEvent.toFixed(2), currencyCode]}
              />
            </div>
          )}
          <TicketGroupInputTable
            purchaseTicketGroupInputField={fieldArray}
            valueOptionsContent={{}}
            listingNotesMap={null}
            purchaseOrderInfo={null}
            currencyCode={currencyCode}
            hideValueColumns
            alwaysUseTotalCost
          />
        </Stack>
      </PlaceholderEventMapContextProvider>
    </FormProvider>
  );
};
